import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { ExtensionsService } from '@pages/checkout-app/services/extensions/extensions.service';
import { ShopService } from '@app/services/shops/shop.service';
import {
  AddProductsModalComponent,
  AddProductsModalConfig,
  SelectableProduct,
} from '@app/sharedModule/product-selection/components/add-products-modal/add-products-modal.component';
import { ProductListItem } from '@storybook/components/product-list/product-list.component';
import {
  BadgeFormGroup,
  BoostGroupFormGroup,
  PRODUCTS_IN_CART_MODE_OPTIONS,
  UrgencyBoostExtension,
  createBadgeFormGroup,
  makeBadgeId,
} from '../../../../model';
import { getExtensionRules } from '../../../../../../../shared/all-extension-rules';

@Component({
  selector: 'boost-group',
  templateUrl: './boost-group.component.html',
  styleUrls: ['./boost-group.component.scss'],
  standalone: false,
})
export class BoostGroupComponent {
  @Input() groupForm: FormGroup<BoostGroupFormGroup>;
  @Input() groupIndex: number;
  @Input() removable = true;
  @Output() removeRequested = new EventEmitter<void>();
  @Output() duplicateRequested = new EventEmitter<void>();

  productsInCartModeOptions = PRODUCTS_IN_CART_MODE_OPTIONS;
  rules = getExtensionRules();
  entities = this.rules.map((r) => r.entity);
  BadgeType = UrgencyBoostExtension.BadgeType;
  expanded = true;

  addBadgeOptions = [
    { label: 'Low stock indicator', value: UrgencyBoostExtension.BadgeType.LOW_STOCK },
    { label: 'Recent Purchases', value: UrgencyBoostExtension.BadgeType.RECENT_PURCHASES },
    { label: 'Custom Message', value: UrgencyBoostExtension.BadgeType.CUSTOM_MESSAGE },
  ];

  onAddBadgeOption(option: { value: UrgencyBoostExtension.BadgeType } | null): void {
    if (option?.value) {
      this.addBadge(option.value);
    }
  }

  constructor(
    private dialog: MatDialog,
    private extensionsService: ExtensionsService,
  ) {}

  toggleExpanded(): void {
    this.expanded = !this.expanded;
  }

  get badgesArray(): FormArray<FormGroup<BadgeFormGroup>> {
    return this.groupForm.controls.badges;
  }

  get currentMode(): UrgencyBoostExtension.ProductsInCartMode {
    return this.groupForm.controls.productsInCart.controls.mode.value;
  }

  setMode(mode: string): void {
    this.groupForm.controls.productsInCart.controls.mode.setValue(mode as UrgencyBoostExtension.ProductsInCartMode);
  }

  get manualProducts(): UrgencyBoostExtension.ManualProductRef[] {
    return this.groupForm.controls.productsInCart.controls.products.value ?? [];
  }

  get manualProductsForDisplay(): ProductListItem[] {
    return this.manualProducts.map((p) => ({
      id: p.id,
      name: p.name ?? String(p.id),
      imageUrl: p.imageUrl,
      selectedOptions: p.selectedOptions,
    }));
  }

  async onAddProducts(): Promise<void> {
    await this.openProductPickerModal(this.manualProducts);
  }

  async onEditProduct(product: ProductListItem): Promise<void> {
    // Edit re-opens the picker pre-selected on this product so the merchant
    // can adjust the variant selection. The modal returns the full set;
    // replacing the array keeps `prioritizeRecommendedItemsBy` order stable.
    await this.openProductPickerModal(this.manualProducts, product.id);
  }

  private async openProductPickerModal(
    current: UrgencyBoostExtension.ManualProductRef[],
    focusProductId?: string | number,
  ): Promise<void> {
    // When editing a single product, that product must NOT be excluded so it
    // re-appears in the modal pre-checked; other already-selected products
    // stay excluded.
    const excludeProductIds = current.filter((p) => p.id !== focusProductId).map((p) => p.id);
    const preSelectedProducts: SelectableProduct[] = current.map((p) => ({
      id: p.id,
      title: p.name ?? String(p.id),
      imageUrl: p.imageUrl,
      selectedOptions: p.selectedOptions,
    }));

    const config: AddProductsModalConfig = {
      title: focusProductId ? 'Edit Product' : 'Add Products',
      searchPlaceholder: 'Search products by name or SKU...',
      addButtonText: 'Select',
      excludeProductIds,
      enableVariants: true,
      enableEdit: true,
      preSelectedProducts,
      fetchProducts: (query) =>
        firstValueFrom(this.extensionsService.queryShopifyForProductsUsingRestrictionRules({ query })),
    };

    const dialogRef = this.dialog.open(AddProductsModalComponent, {
      data: config,
      panelClass: 'add-products-modal-container',
      width: '700px',
      maxHeight: '80vh',
    });

    const result: SelectableProduct[] | undefined = await firstValueFrom(dialogRef.afterClosed());
    if (!result?.length) return;

    const merged: UrgencyBoostExtension.ManualProductRef[] = result.map((p) => ({
      id: p.id,
      name: p.title,
      imageUrl: p.imageUrl,
      ...(p.selectedOptions?.length ? { selectedOptions: p.selectedOptions } : {}),
    }));
    this.groupForm.controls.productsInCart.controls.products.setValue(merged);
  }

  onRemoveProduct(productId: string | number): void {
    const next = this.manualProducts.filter((p) => p.id !== productId);
    this.groupForm.controls.productsInCart.controls.products.setValue(next);
  }

  onExternalLink(product: ProductListItem): void {
    const productId =
      typeof product.id === 'string' && product.id.includes('/') ? product.id.split('/').pop() : product.id;
    const shop = this.shopService.shopSignal();
    const trimmedShopName = shop.name.split('.myshopify.com')[0];
    window.open(`https://admin.shopify.com/store/${trimmedShopName}/products/${productId}`, '_blank');
  }

  private shopService = inject(ShopService);

  addBadge(type: UrgencyBoostExtension.BadgeType): void {
    if (this.badgesArray.length >= UrgencyBoostExtension.MAX_BADGES_PER_GROUP) return;
    this.badgesArray.push(createBadgeFormGroup({ type }));
  }

  removeBadge(index: number): void {
    this.badgesArray.removeAt(index);
  }

  duplicateBadge(index: number): void {
    if (this.badgesArray.length >= UrgencyBoostExtension.MAX_BADGES_PER_GROUP) return;
    const source = this.badgesArray.at(index);
    const cloned = createBadgeFormGroup({
      ...source.value,
      id: makeBadgeId(),
    } as Partial<UrgencyBoostExtension.Badge>);
    this.badgesArray.insert(index + 1, cloned);
    this.badgesArray.markAsDirty();
  }

  reorderBadges(event: CdkDragDrop<unknown[]>): void {
    if (event.previousIndex === event.currentIndex) return;
    // moveItemInArray on FormArray.controls bypasses Angular's internal
    // tracking; removeAt + insert keeps the FormArray's parent links and
    // validity in sync. See: https://github.com/angular/angular/issues/27171
    const control = this.badgesArray.at(event.previousIndex);
    this.badgesArray.removeAt(event.previousIndex, { emitEvent: false });
    this.badgesArray.insert(event.currentIndex, control);
    this.badgesArray.markAsDirty();
  }

  trackByBadgeId(_: number, badge: FormGroup<BadgeFormGroup>): string {
    return badge.controls.id.value;
  }
}
